import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import JoinLeagueModal from './JoinLeagueModal';
import CreateLeagueModal from './CreateLeagueModal';

interface MyLeague {
  id: number;
  name: string;
  league_type: string;
  live_tournament_id: number;
  tournament_name: string;
  max_participants: number;
  current_participants: number;
  status: string;
  is_public: boolean;
  join_code?: string;
  passcode?: string;
  created_by: number;
  is_creator: boolean;
  joined_at: string;
  created_at: string;
  /** From API: tournament / H2H lifecycle complete or abandoned */
  is_past_league?: boolean;
  h2h_status?: string;
  h2h_type?: string;
  deleted_at?: string | null;
}

const MyLeagues: React.FC = () => {
  const [leagues, setLeagues] = useState<MyLeague[]>([]);
  const [publicLeagues, setPublicLeagues] = useState<MyLeague[]>([]);
  const [loading, setLoading] = useState(true);
  const [publicLoading, setPublicLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [publicError, setPublicError] = useState<string | null>(null);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState<MyLeague | null>(null);
  const [showJoinCode, setShowJoinCode] = useState<number | null>(null);
  const [showPasscode, setShowPasscode] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'my' | 'past' | 'public'>('my');
  const [showCreateLeagueModal, setShowCreateLeagueModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMyLeagues();
    fetchPublicLeagues();
  }, []);

  useEffect(() => {
    if (activeTab === 'public' && publicLeagues.length === 0) {
      fetchPublicLeagues();
    }
  }, [activeTab]);

  const fetchMyLeagues = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiService.getMyLeagues();

      if (data.success) {
        setLeagues(data.leagues);
      } else {
        setError(data.message || 'Failed to load leagues');
      }
    } catch (err) {
      setError('Failed to load leagues');
      console.error('Error fetching leagues:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPublicLeagues = async () => {
    try {
      setPublicLoading(true);
      setPublicError(null);

      const data = await apiService.getFantasyLeagues();

      if (data.success) {
        const leagues = data.tournaments || data.fantasyLeagues || [];
        console.log('🔍 Public leagues data:', leagues);
        setPublicLeagues(leagues);
      } else {
        setPublicError(data.message || 'Failed to load public leagues');
      }
    } catch (err) {
      setPublicError('Failed to load public leagues');
      console.error('Error fetching public leagues:', err);
    } finally {
      setPublicLoading(false);
    }
  };

  const resolveLeagueType = (leagueType: string | undefined) =>
    leagueType && String(leagueType).trim() ? leagueType : 'pickem';

  const handleViewLeague = (leagueId: number, leagueType: string) => {
    console.log('🔍 Picks (league) clicked:', { leagueId, leagueType });

    leagueType = resolveLeagueType(leagueType);

    if (leagueType === 'pickem') {
      navigate(`/pickem-league/${leagueId}?tab=picks`);
    } else if (leagueType === 'eliminator') {
      navigate(`/eliminator-league/${leagueId}?tab=mypicks`);
    } else if (leagueType === 'budget_league') {
      navigate(`/budget-league/${leagueId}?tab=team`);
    } else if (leagueType === 'h2h') {
      navigate(`/h2h-league/${leagueId}?tab=myteam`);
    } else {
      // For other league types, show a message that individual league pages are not available yet
      alert(`Individual league pages for ${leagueType} leagues are not available yet. You can view these leagues in the tournaments section.`);
      navigate(`/tournaments`);
    }
  };

  const handleJoinSuccess = () => {
    fetchMyLeagues();
    // Also refresh public leagues to update the join/view buttons
    if (activeTab === 'public') {
      fetchPublicLeagues();
    }
  };

  const hasUserJoinedLeague = (leagueId: number) => {
    return leagues.some(league => league.id === leagueId);
  };

  const activeLeagues = leagues.filter((l) => !l.is_past_league);
  const pastLeagues = leagues.filter((l) => !!l.is_past_league);

  const primaryNavLabel = (leagueType: string) =>
    leagueType === 'h2h' ? 'Team' : 'Picks';

  const secondaryNavLabel = (leagueType: string) =>
    leagueType === 'h2h' ? 'H2H' : 'Standings';

  const handleJoinLeague = (league: MyLeague) => {
    setSelectedLeague(league);
    setShowJoinModal(true);
  };

  /** Opens the league page on the Standings tab (or closest equivalent per league type). */
  const handleViewStandings = (leagueId: number, leagueType: string) => {
    const lt = resolveLeagueType(leagueType);
    if (lt === 'pickem') {
      navigate(`/pickem-league/${leagueId}?tab=standings`);
    } else if (lt === 'eliminator') {
      navigate(`/eliminator-league/${leagueId}?tab=standings`);
    } else if (lt === 'budget_league') {
      navigate(`/budget-league/${leagueId}?tab=standings`);
    } else if (lt === 'h2h') {
      navigate(`/h2h-league/${leagueId}?tab=h2h`);
    } else {
      alert(`Standings for ${lt} leagues are not available yet.`);
    }
  };

  const handleCreateLeague = () => {
    setShowCreateLeagueModal(true);
  };

  const handleLeagueCreated = () => {
    // Refresh the leagues list
    fetchMyLeagues();
    if (activeTab === 'public') {
      fetchPublicLeagues();
    }
  };

  const copyJoinCode = async (joinCode: string) => {
    try {
      await navigator.clipboard.writeText(joinCode);
      alert('Join code copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = joinCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Join code copied to clipboard!');
    }
  };

  const copyPasscode = async (passcode: string) => {
    try {
      await navigator.clipboard.writeText(passcode);
      alert('Passcode copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = passcode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Passcode copied to clipboard!');
    }
  };

  const getLeagueTypeInfo = (type: string) => {
    switch (type) {
      case 'pickem':
        return { label: "Pick'em", icon: '🎯', color: 'bg-purple-600' };
      case 'budget_league':
        return { label: 'Budget League', icon: '💰', color: 'bg-green-600' };
      case 'draft_league':
        return { label: 'Draft League', icon: '📋', color: 'bg-blue-600' };
      case 'h2h':
        return { label: 'H2H', icon: '⚔️', color: 'bg-red-600' };
      case 'eliminator':
        return { label: 'Eliminator', icon: '💀', color: 'bg-orange-600' };
      default:
        return { label: type, icon: '🏏', color: 'bg-gray-600' };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 text-xl mb-2">⚠️</div>
        <p className="text-white mb-4">{error}</p>
        <button 
          onClick={fetchMyLeagues}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="w-full py-2 sm:py-6 px-2 sm:px-4">
      <div className="mb-5 sm:mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Leagues</h1>
          <p className="text-white/60">Manage your fantasy cricket leagues</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleCreateLeague}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Create League
          </button>
        <button
          onClick={() => setShowJoinModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Join League
        </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/20 mb-4 sm:mb-6 px-1">
        <nav className="flex flex-wrap gap-x-8 gap-y-2">
          <button
            onClick={() => setActiveTab('my')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'my'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-white/60 hover:text-white/80'
            }`}
          >
            My Leagues ({activeLeagues.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'past'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-white/60 hover:text-white/80'
            }`}
          >
            Past Leagues ({pastLeagues.length})
          </button>
          <button
            onClick={() => setActiveTab('public')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'public'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-white/60 hover:text-white/80'
            }`}
          >
            Public Leagues ({publicLeagues.length})
          </button>
        </nav>
      </div>

      {/* Welcome message for new users */}
      {activeTab === 'my' && activeLeagues.length === 0 && leagues.length === 0 && (
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <div className="text-blue-400 text-xl">🎯</div>
            <div>
              <h3 className="text-white font-medium mb-1">Welcome to Fantasy Cricket!</h3>
              <p className="text-blue-200 text-sm">
                Get started by joining a league using a join code from a friend, or browse public leagues in tournaments.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* My Leagues Tab Content — active only */}
      {activeTab === 'my' && (
        <>
          {activeLeagues.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏏</div>
              <p className="text-xl text-white mb-2">
                {leagues.length === 0 ? 'No leagues yet' : 'No active leagues'}
              </p>
              <p className="text-blue-200 mb-6">
                {leagues.length === 0
                  ? 'Get started by joining a league using a join code, or browse public leagues'
                  : 'Finished or abandoned leagues are listed under Past Leagues.'}
              </p>

              {leagues.length > 0 && pastLeagues.length > 0 && (
                <button
                  type="button"
                  onClick={() => setActiveTab('past')}
                  className="mb-6 bg-slate-600 hover:bg-slate-500 text-white px-6 py-2 rounded-lg text-sm font-medium"
                >
                  View past leagues ({pastLeagues.length})
                </button>
              )}

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center">
                <button
                  onClick={() => setShowJoinModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg transition-colors text-sm sm:text-base font-medium"
                >
                  Join League with Code
                </button>
                <button
                  onClick={() => navigate('/tournaments')}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg transition-colors text-sm sm:text-base font-medium"
                >
                  Browse Public Leagues
                </button>
                <button
                  onClick={() => navigate('/create-league')}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg transition-colors text-sm sm:text-base font-medium"
                >
                  Create New League
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {activeLeagues.map((league) => {
                const typeInfo = getLeagueTypeInfo(league.league_type);
                const pLabel = primaryNavLabel(league.league_type);
                const sLabel = secondaryNavLabel(league.league_type);

                return (
                  <div
                    key={league.id}
                    onClick={() => handleViewLeague(league.id, league.league_type)}
                    className="bg-white/10 backdrop-blur-md rounded-none sm:rounded-lg p-3 sm:p-5 border-x-0 sm:border-x border-y border-white/20 hover:bg-white/15 transition-all duration-200 cursor-pointer"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <span className="text-2xl" aria-hidden="true">
                            {typeInfo.icon}
                          </span>
                          <div>
                            <h3 className="text-lg font-semibold text-white">{league.name}</h3>
                            <div className="flex items-center space-x-2 mt-1 flex-wrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color} text-white`}>
                                {typeInfo.label}
                              </span>
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  league.is_creator ? 'bg-yellow-600 text-white' : 'bg-blue-600 text-white'
                                }`}
                              >
                                {league.is_creator ? 'Created by you' : 'Joined'}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="text-white/60">Tournament:</span>
                            <div className="text-white font-medium">{league.tournament_name}</div>
                          </div>
                          <div>
                            <span className="text-white/60">Participants:</span>
                            <div className="text-white font-medium">
                              {league.current_participants}/{league.max_participants}
                            </div>
                          </div>
                          <div className="hidden md:block">
                            <span className="text-white/60">Status:</span>
                            <div className="text-white font-medium capitalize">{league.status}</div>
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-2 lg:flex-col lg:space-x-0 lg:space-y-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewLeague(league.id, league.league_type);
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          {pLabel}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewStandings(league.id, league.league_type);
                          }}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          {sLabel}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Past Leagues */}
      {activeTab === 'past' && (
        <>
          {pastLeagues.length === 0 ? (
            <div className="text-center py-12 text-white/80">
              <p className="text-lg">No past leagues yet.</p>
              <p className="text-sm text-white/60 mt-2">
                Leagues appear here when the tournament is finished, or when an H2H league is completed or abandoned.
              </p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {pastLeagues.map((league) => {
                const typeInfo = getLeagueTypeInfo(league.league_type);
                const pLabel = primaryNavLabel(league.league_type);
                const sLabel = secondaryNavLabel(league.league_type);

                return (
                  <div
                    key={league.id}
                    onClick={() => handleViewLeague(league.id, league.league_type)}
                    className="bg-white/10 backdrop-blur-md rounded-none sm:rounded-lg p-3 sm:p-5 border-x-0 sm:border-x border-y border-white/20 border-amber-900/40 hover:bg-white/15 transition-all duration-200 cursor-pointer"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <span className="text-2xl" aria-hidden="true">
                            {typeInfo.icon}
                          </span>
                          <div>
                            <h3 className="text-lg font-semibold text-white">{league.name}</h3>
                            <div className="flex items-center space-x-2 mt-1 flex-wrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color} text-white`}>
                                {typeInfo.label}
                              </span>
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-800/80 text-amber-100">
                                Past
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-white/60">Tournament:</span>
                            <div className="text-white font-medium">{league.tournament_name}</div>
                          </div>
                          <div>
                            <span className="text-white/60">Participants:</span>
                            <div className="text-white font-medium">
                              {league.current_participants}/{league.max_participants}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-2 lg:flex-col lg:space-x-0 lg:space-y-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewLeague(league.id, league.league_type);
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          {pLabel}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewStandings(league.id, league.league_type);
                          }}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          {sLabel}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Public Leagues Tab Content */}
      {activeTab === 'public' && (
        <>
          {publicLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : publicError ? (
            <div className="text-center py-12">
              <div className="text-red-400 text-xl mb-2">⚠️</div>
              <p className="text-white mb-4">{publicError}</p>
              <button 
                onClick={fetchPublicLeagues}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : publicLeagues.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏏</div>
              <p className="text-xl text-white mb-2">No public leagues found</p>
              <p className="text-blue-200 mb-6">
                Check back later for new public leagues or create your own
              </p>
              <button
                onClick={handleCreateLeague}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors font-medium"
              >
                Create New League
              </button>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {publicLeagues.map(league => {
                // Try different possible field names for league type
                const leagueType = league.league_type || (league as any).tournament_type || (league as any).type || 'pickem';
                const typeInfo = getLeagueTypeInfo(leagueType);
                const pLabel = primaryNavLabel(leagueType);
                const sLabel = secondaryNavLabel(leagueType);
                console.log('🔍 Rendering public league:', { 
                  id: league.id, 
                  name: league.name, 
                  league_type: league.league_type,
                  tournament_type: (league as any).tournament_type,
                  type: (league as any).type,
                  final_league_type: leagueType,
                  typeInfo 
                });
                
                const joined = hasUserJoinedLeague(league.id);

                return (
                  <div
                    key={league.id}
                    onClick={joined ? () => handleViewLeague(league.id, leagueType) : undefined}
                    className={`bg-white/10 backdrop-blur-md rounded-none sm:rounded-lg p-3 sm:p-5 border-x-0 sm:border-x border-y border-white/20 hover:bg-white/15 transition-all duration-200${
                      joined ? ' cursor-pointer' : ''
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                      {/* Left side - League info */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <span className="text-2xl" aria-hidden="true">
                            {typeInfo.icon}
                          </span>
                          <div>
                            <h3 className="text-lg font-semibold text-white">{league.name}</h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color} text-white`}>
                                {typeInfo.label}
                              </span>
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-600 text-white">
                                Public
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="text-white/60">Tournament:</span>
                            <div className="text-white font-medium">{league.tournament_name}</div>
                          </div>
                          <div>
                            <span className="text-white/60">Participants:</span>
                            <div className="text-white font-medium">{league.current_participants}/{league.max_participants}</div>
                          </div>
                          <div className="hidden md:block">
                            <span className="text-white/60">Created:</span>
                            <div className="text-white font-medium">{formatDate(league.created_at)}</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Right side - Action buttons */}
                      <div className="flex space-x-2 lg:flex-col lg:space-x-0 lg:space-y-2">
                        {joined ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewLeague(league.id, leagueType);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            {pLabel}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleJoinLeague(league);
                            }}
                            disabled={league.current_participants >= league.max_participants}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              league.current_participants >= league.max_participants
                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                : 'bg-green-600 hover:bg-green-700 text-white'
                            }`}
                          >
                            {league.current_participants >= league.max_participants ? 'League Full' : 'Join League'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewStandings(league.id, leagueType);
                          }}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          {sLabel}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Join League Modal */}
      <JoinLeagueModal
        isOpen={showJoinModal}
        onClose={() => {
          setShowJoinModal(false);
          setSelectedLeague(null);
        }}
        onSuccess={handleJoinSuccess}
        leagueId={selectedLeague?.id}
        isPublicLeague={selectedLeague?.is_public || false}
        leagueType={selectedLeague?.league_type}
      />

      {/* Create League Modal */}
      <CreateLeagueModal
        isOpen={showCreateLeagueModal}
        onClose={() => setShowCreateLeagueModal(false)}
        onLeagueCreated={handleLeagueCreated}
      />
    </div>
  );
};

export default MyLeagues; 